import React, {useEffect, useRef, useState} from 'react'
import ApexCharts from 'apexcharts'
import moment from 'moment'
import 'moment/locale/fr'
import {getCSS, getCSSVariableValue} from '../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {getKPI, getSearchPieceTypePiece} from '../../../../components/requests'
import {SearchPiece} from '../../../../components/SearchPiece'
import {InfoDemandeKPI} from '../../../../components/Models'

moment.locale('fr')

type ChartFilter = 'day' | 'week' | 'month' | 'year'

const ChartDashboard2: React.FC = () => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()
  const [filter, setFilter] = useState<ChartFilter>('day')
  const [chartInstance, setChartInstance] = useState<ApexCharts | null>(null)
  const [currentFilterLabel, setCurrentFilterLabel] = useState<string>('Jour')
  const [currentData, setCurrentData] = useState<InfoDemandeKPI>()
  const [pieceTypePiece, setPieceTypePiece] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>('')
  const [selectedSearchValue, setSelectedSearchValue] = useState<any>('')
  const [pieceSpinner, setPieceSpinner] = useState<boolean>(false)
  const [resultShowPiece, setResultShowPiece] = useState<boolean>(false)
  const [clearInput, setClearInput] = useState<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const fetchChartData = async (filter: ChartFilter, pieceSelected) => {
    try {
      const response = await getKPI(filter, pieceSelected)
      if (response?.status === 200 && response?.data) {
        const data: InfoDemandeKPI[] = response.data
        const categories = data.map((item) => item.label) // Jour, Semaine, Mois, ou Année
        const seriesData = [{name: 'Total demandes', data: data.map((item) => item.counts.total)}]
        let label = ''
        const lastItem = data[data.length - 1]
        if (lastItem) {
          setCurrentData(lastItem)
          label = lastItem.label
        }

        if (chartInstance) {
          chartInstance.updateOptions({
            chart: {
              events: {
                click: (event, chartContext, config) => {
                  const dataPointIndex = config.dataPointIndex
                  const labelValue = categories[dataPointIndex]
                  if (labelValue) {
                    label = labelValue
                    const clickedData = data.find((item) => item.label === labelValue)
                    if (clickedData) {
                      setCurrentData(clickedData)
                    }
                    chartInstance.updateOptions({
                      xaxis: {
                        categories,
                        labels: {
                          formatter: function (val) {
                            if (val === label) {
                              return `-> ${val} <-`
                            }
                            return val
                          },
                          style: {
                            colors: categories.map((cat) =>
                              cat === label ? '#FF0000' : getCSSVariableValue('--bs-gray-500')
                            ),
                          },
                        },
                      },
                    })
                  }
                },
              },
            },
            xaxis: {
              categories,
              labels: {
                formatter: function (val) {
                  if (val === label) {
                    return `-> ${val} <-`
                  }
                  return val
                },
                style: {
                  colors: categories.map((cat) =>
                    cat === label ? '#FF0000' : getCSSVariableValue('--bs-gray-500')
                  ),
                },
              },
            },
            tooltip: {
              custom: ({dataPointIndex}) => {
                const item = data[dataPointIndex]
                const showAll = selectedSearchValue === ''
                const createBadge = (label, count, badgeClass) => {
                  return `
                    <div style="display: flex; justify-content: space-between; align-items: center; width: 100%; margin-top: 10px;">
                      <span class="badge ${badgeClass}" style="font-size: 10px; flex-grow: 1; text-align: left;">● ${label.toUpperCase()} : ${count}</span>
                    </div>
                  `
                }
                return `
                    <div style="padding: 8px; font-size: 13px; font-family: Arial, sans-serif;">
                      <div style="text-align: center; padding: 8px; background-color: #0d6efd; color: white; border-radius: 6px; font-weight: bold;">
                        ${currentFilterLabel} : ${item.label}
                      </div>
                      <div style="margin-top: 10px;">
                        ${
                          showAll
                            ? createBadge('Nouvelle', item.counts.nouvelle, 'badge-light')
                            : ''
                        }
                        ${
                          showAll
                            ? createBadge('Assignée', item.counts.assignee, 'badge-light-warning')
                            : ''
                        }
                        ${createBadge('Proposée', item.counts.proposee, 'badge-info')}
                        ${createBadge('Acceptée', item.counts.acceptee, 'badge-light-success')}
                        ${createBadge('Refusée', item.counts.refusee, 'badge-light-danger')}
                        ${
                          showAll
                            ? createBadge(
                                'Clôture Positive',
                                item.counts.cloturePositive,
                                'badge-success'
                              )
                            : ''
                        }
                        ${
                          showAll
                            ? createBadge(
                                'Clôture Négative',
                                item.counts.clotureNegative,
                                'badge-danger'
                              )
                            : ''
                        }
                      </div>
                    </div>
                  `
              },
            },
          })

          chartInstance.updateSeries(seriesData)
        }
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données', error)
    }
  }

  const initChart = () => {
    const height = chartRef.current ? parseInt(getCSS(chartRef.current, 'height')) : 500

    const options: ApexCharts.ApexOptions = {
      chart: {
        type: 'area',
        height,
        toolbar: {show: true},
        fontFamily: 'inherit',
        stacked: true,
      },
      series: [],
      xaxis: {
        categories: [],
        labels: {
          style: {
            colors: getCSSVariableValue('--bs-gray-500'),
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: getCSSVariableValue('--bs-gray-500'),
          },
        },
      },
      tooltip: {
        custom: () => 'Chargement...',
      },
      fill: {
        type: 'gradient',
        gradient: {
          opacityFrom: 0.6,
          opacityTo: 0.8,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      noData: {
        text: 'Chargement...',
      },
      legend: {
        show: true,
      },
    }

    if (chartRef.current) {
      const chart = new ApexCharts(chartRef.current, options)
      chart.render()
      setChartInstance(chart)
    }
  }

  useEffect(() => {
    initChart()
  }, [mode])

  useEffect(() => {
    if (chartInstance) {
      fetchChartData(filter, selectedSearchValue)
    }
  }, [filter, chartInstance, selectedSearchValue])

  // gestion recherche de pièce par type pièce
  const searchPieceTypePiece = (e) => {
    const inputValue = e.target.value
    setSearchValue(inputValue)
    setSelectedSearchValue('')
    setClearInput(false)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue !== '') {
        setPieceSpinner(true)
        getSearchPieceTypePiece(inputValue)
          .then((res) => {
            if (res?.status === 200) {
              if (res?.data !== undefined) {
                setResultShowPiece(true)
                setPieceTypePiece(res.data)
                setPieceSpinner(false)
              }
            }
          })
          .catch(() => {
            console.log('error')
          })
      }
    }, 800)
  }

  const renderBadgeCard = (label, count, badgeClass = '') => (
    <div className='col-md-6 col-lg-3 col-xlg-3'>
      <div className='card card-hover mb-6'>
        <div className='p-2 rounded text-center'>
          <h3 className='mb-0 fw-light' style={{fontWeight: '400'}}>
            <span
              className={`badge ${badgeClass}`}
              style={{fontSize: '14px', padding: '10px', display: 'block', textAlign: 'center'}}
            >
              <div style={{fontSize: '18px', fontWeight: '600'}}>{count}</div>
              <div style={{fontSize: '12px', marginTop: '5px'}}>{label.toUpperCase()}</div>
            </span>
          </h3>
        </div>
      </div>
    </div>
  )

  return (
    <div className='w-100'>
      <div className='card-header border-0 pt-5 justify-content-between'>
        <div>
          <h3 className='card-title fw-bold fs-3 mb-1'>Statistiques sur les demandes de pièce</h3>
          <span className='text-muted fw-semibold fs-7'>
            {moment().format('MMMM YYYY').toUpperCase()}
          </span>
        </div>
        <div className='border-0 gap-4 flex-wrap justify-content-end'>
          <div className='gap-4 flex-wrap justify-content-end'>
            {/* Input de recherche pièce */}
            <div style={{minWidth: '400px'}}>
              <SearchPiece
                inputClassName={'form-control'}
                placeholder={'Recherche pièce'}
                onChange={(e) => searchPieceTypePiece(e)}
                resultatPiece={pieceTypePiece}
                value={selectedSearchValue || searchValue}
                setSearchValue={setSearchValue}
                pieceSpinner={pieceSpinner}
                resultShowPiece={resultShowPiece}
                clearInput={clearInput}
                selectedSearchValue={selectedSearchValue}
                showPieceHn={true}
                onSelect={(pieceTypePiece) => {
                  setSearchValue(pieceTypePiece.nom)
                  setSelectedSearchValue(pieceTypePiece.nom)
                  setResultShowPiece(false)
                  setClearInput(true)
                }}
              />
            </div>

            {/* Boutons de filtre */}
            <div className='card-toolbar justify-content-end' data-kt-buttons='true'>
              {(['day', 'week', 'month', 'year'] as ChartFilter[]).map((f) => (
                <a
                  key={f}
                  className={`btn btn-sm ${
                    filter === f ? 'btn-active-primary active' : 'btn-color-muted'
                  }`}
                  onClick={() => {
                    setFilter(f)
                    setCurrentFilterLabel(
                      f === 'day'
                        ? 'Jour'
                        : f === 'week'
                        ? 'Semaine'
                        : f === 'month'
                        ? 'Mois'
                        : 'Année'
                    )
                  }}
                >
                  {f === 'day'
                    ? 'Jour'
                    : f === 'week'
                    ? 'Semaine'
                    : f === 'month'
                    ? 'Mois'
                    : 'Année'}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div ref={chartRef} style={{height: '500px'}} className='card-rounded-bottom'></div>

      {/* Statistiques par statut */}
      <div className='row mt-4'>
        {selectedSearchValue === '' &&
          renderBadgeCard('Nouvelle', currentData?.counts?.nouvelle ?? 0, 'badge-light')}
        {selectedSearchValue === '' &&
          renderBadgeCard('Assignée', currentData?.counts?.assignee ?? 0, 'badge-light-warning')}
        {renderBadgeCard('Proposée', currentData?.counts?.proposee ?? 0, 'badge-info')}
        {renderBadgeCard('Acceptée', currentData?.counts?.acceptee ?? 0, 'badge-light-success')}
        {renderBadgeCard('Refusée', currentData?.counts?.refusee ?? 0, 'badge-light-danger')}
        {selectedSearchValue === '' &&
          renderBadgeCard(
            'Clôture Positive',
            currentData?.counts?.cloturePositive ?? 0,
            'badge-success'
          )}
        {selectedSearchValue === '' &&
          renderBadgeCard(
            'Clôture Négative',
            currentData?.counts?.clotureNegative ?? 0,
            'badge-danger'
          )}
      </div>
    </div>
  )
}

export {ChartDashboard2}
